import noop from 'lodash/noop';
import { useCallback, useMemo } from 'react';
import { useController } from 'react-hook-form';

import { MedicalLeavePuePayer } from '@dreryk/egabinet-ezla-frontend-api';

import { createInterpolatedTranslationKey, FieldController, useTranslate } from '@dreryk/react-common';
import {
   Table,
   TableColumnConfiguration,
   TableKeyExtractor,
   UncontrolledToggle,
   ValueRenderer,
} from '@dreryk/react-components';

import { commonTranslationKeys } from '../../../../../translations';
import { LeaveAfterTerminationOfEmploymentRecord, MedicalLeaveCommonFormController } from '../../common.types';

import { StyledMedicalLeaveFormPayersTable } from './StyledMedicalLeaveFormPayersTable';
import { StyledMedicalLeaveFormTableToggle } from './StyledMedicalLeaveFormTableToggle';

type MedicalLeaveFormPayersTableProps = {
   readonly controller: MedicalLeaveCommonFormController;
   readonly selectedPayers: MedicalLeavePuePayer[];
};

const useTableColumns = (
   controller: FieldController<LeaveAfterTerminationOfEmploymentRecord>
): TableColumnConfiguration<MedicalLeavePuePayer>[] => {
   const {
      field: { onChange, value },
   } = useController({
      name: controller.name,
      rules: controller.validationRules,
      defaultValue: {},
      control: controller.control,
   });
   const setLeaveAfterTerminationOfEmploymentPayer = useCallback(
      (payer: MedicalLeavePuePayer, selected: boolean): void => {
         onChange({ ...value, [payer.identificationNumber]: selected });
      },
      [onChange, value]
   );
   const translate = useTranslate();

   const leaveAfterTerminationOfEmploymentRenderer: ValueRenderer<MedicalLeavePuePayer, string> = useCallback(
      ({ bean }) => {
         return (
            <StyledMedicalLeaveFormTableToggle>
               <UncontrolledToggle
                  onActiveChange={(active: boolean) => setLeaveAfterTerminationOfEmploymentPayer(bean, active)}
                  active={value[bean.identificationNumber]}
                  testId={'leaveAfterTerminationOfEmployment'}
               />
            </StyledMedicalLeaveFormTableToggle>
         );
      },
      [setLeaveAfterTerminationOfEmploymentPayer, value]
   );

   return [
      {
         label: commonTranslationKeys.payerName,
         width: 'calc(100% - 275px)',
         accessor: (payer) =>
            translate(
               createInterpolatedTranslationKey(
                  commonTranslationKeys[payer.name ? 'payerNameLabel' : 'payerIdentificationNumberLabel'],
                  {
                     name: payer.name,
                     identificationType: translate(commonTranslationKeys[payer.identificationNumberType]),
                     identificationNumber: payer.identificationNumber,
                  }
               )
            ),
         name: 'payerName',
         sortingDisabled: true,
      },
      {
         label: commonTranslationKeys.leaveAfterTerminationOfEmployment,
         width: '275px',
         renderer: leaveAfterTerminationOfEmploymentRenderer,
         name: 'leaveAfterTerminationOfEmployment',
         accessor: noop,
         sortingDisabled: true,
      },
   ];
};

const keyExtractor: TableKeyExtractor<MedicalLeavePuePayer> = (bean) =>
   `${bean.identificationNumberType}-${bean.identificationNumber}`;

const MedicalLeaveFormPayersTable = (props: MedicalLeaveFormPayersTableProps): JSX.Element | null => {
   const columns = useTableColumns(props.controller.register('leaveAfterTerminationOfEmployment'));
   const dataConfiguration = useMemo<{ data: MedicalLeavePuePayer[] }>(() => ({ data: props.selectedPayers }), [
      props.selectedPayers,
   ]);

   return props.selectedPayers?.length > 0 ? (
      <StyledMedicalLeaveFormPayersTable>
         <Table<MedicalLeavePuePayer>
            columns={columns}
            dataConfiguration={dataConfiguration}
            keyExtractor={keyExtractor}
            testId='payers'
         />
      </StyledMedicalLeaveFormPayersTable>
   ) : null;
};

export { MedicalLeaveFormPayersTable };
